import { httpClient } from './http-client';
import type {
  RecurringTransaction,
  CreateRecurringTransactionRequest,
  UpdateRecurringTransactionRequest,
} from '../types/api';

/**
 * Service for managing recurring transactions (subscriptions, bills, etc.).
 *
 * Communicates with the /recurring-transactions backend API endpoints.
 */
export class RecurringTransactionsService {
  private static readonly BASE_URL = '/recurring-transactions';

  /**
   * List recurring transactions for the current household.
   *
   * Args:
   *     activeOnly (boolean): When true, only return active transactions. Defaults to true.
   *
   * Returns:
   *     Object containing recurring_transactions array and total count.
   */
  static async list(
    activeOnly = true
  ): Promise<{ recurring_transactions: RecurringTransaction[]; total: number }> {
    const url = `${RecurringTransactionsService.BASE_URL}?active_only=${activeOnly}`;
    const raw = await httpClient.get<any>(url);
    return (raw as any).data || raw;
  }

  /**
   * Create a new recurring transaction.
   *
   * Args:
   *     data (CreateRecurringTransactionRequest): The recurring transaction data.
   *
   * Returns:
   *     RecurringTransaction: The created recurring transaction.
   */
  static async create(
    data: CreateRecurringTransactionRequest
  ): Promise<RecurringTransaction> {
    const raw = await httpClient.post<any>(RecurringTransactionsService.BASE_URL, data);
    return (raw as any).data || raw;
  }

  /**
   * Update an existing recurring transaction.
   *
   * Args:
   *     id (string): The recurring transaction ID.
   *     data (UpdateRecurringTransactionRequest): The fields to update.
   *
   * Returns:
   *     RecurringTransaction: The updated recurring transaction.
   */
  static async update(
    id: string,
    data: UpdateRecurringTransactionRequest
  ): Promise<RecurringTransaction> {
    const raw = await httpClient.put<any>(
      `${RecurringTransactionsService.BASE_URL}/${id}`,
      data
    );
    return (raw as any).data || raw;
  }

  /**
   * Delete a recurring transaction.
   *
   * Args:
   *     id (string): The recurring transaction ID.
   */
  static async delete(id: string): Promise<void> {
    await httpClient.delete(`${RecurringTransactionsService.BASE_URL}/${id}`);
  }

  /**
   * Get upcoming recurring transactions due in the next 30 days.
   *
   * Returns:
   *     Object containing upcoming array and total count.
   */
  static async getUpcoming(): Promise<{
    upcoming: RecurringTransaction[];
    total: number;
  }> {
    const raw = await httpClient.get<any>(
      `${RecurringTransactionsService.BASE_URL}/upcoming`
    );
    return (raw as any).data || raw;
  }

  /**
   * Detect recurring patterns from transaction history.
   *
   * Returns:
   *     Object containing detected patterns array and total count.
   */
  static async detectPatterns(): Promise<{
    detected: RecurringTransaction[];
    total: number;
  }> {
    const raw = await httpClient.post<any>(
      `${RecurringTransactionsService.BASE_URL}/detect`
    );
    return (raw as any).data || raw;
  }
}
